import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, 
  X, 
  Check, 
  CheckCheck, 
  FileText, 
  Clock,
  User,
  Calendar
} from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { format, formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead,
    clearAllNotifications
  } = useNotifications();
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }

    // Navigate to specific timesheet based on user role and notification type
    if (notification.timesheetId) {
      // Navigate to timesheet details page based on role
      if (user?.role === 'SUPER_ADMIN') {
        navigate(`/super-admin/timesheets/${notification.timesheetId}`);
      } else if (user?.role === 'ADMIN') {
        navigate(`/admin/timesheets/${notification.timesheetId}`);
      } else if (user?.role === 'MANAGER') {
        navigate(`/manager/timesheets/${notification.timesheetId}`);
      } else if (user?.role === 'EMPLOYEE') {
        // For employees, navigate to their timesheet details
        navigate(`/timesheets/${notification.timesheetId}`);
      } else {
        // Fallback - try to determine route from notification type
        if (notification.type === 'timesheet_submitted') {
          navigate('/admin/timesheets/review');
        } else {
          navigate('/timesheets');
        }
      }
    } else {
      // No timesheet ID, navigate to general pages
      if (user?.role === 'MANAGER') {
        navigate('/manager/timesheets');
      } else if (user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') {
        navigate('/admin/timesheets/review');
      } else {
        navigate('/timesheets');
      }
    }

    setIsOpen(false);
  };

  const getNotificationIcon = (type) => {
    const typeUpper = type?.toUpperCase();
    switch (typeUpper) {
      case 'TIMESHEET_SUBMITTED':
        return <FileText className="w-4 h-4 text-blue-500" />;
      case 'TIMESHEET_APPROVED':
        return <Check className="w-4 h-4 text-green-500" />;
      case 'TIMESHEET_REJECTED':
        return <X className="w-4 h-4 text-red-500" />;
      case 'TIMESHEET_REVOKED':
        return <X className="w-4 h-4 text-orange-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const getNotificationColor = (type) => {
    const typeUpper = type?.toUpperCase();
    switch (typeUpper) {
      case 'TIMESHEET_SUBMITTED':
        return 'bg-blue-50 border-blue-200';
      case 'TIMESHEET_APPROVED':
        return 'bg-green-50 border-green-200';
      case 'TIMESHEET_REJECTED':
        return 'bg-red-50 border-red-200';
      case 'TIMESHEET_REVOKED':
        return 'bg-orange-50 border-orange-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="relative">
      {/* Notification Bell */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.span>
        )}
      </motion.button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
              <div className="flex items-center space-x-2">
                {notifications.length > 0 && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      if (window.confirm('Are you sure you want to clear all notifications? This action cannot be undone.')) {
                        clearAllNotifications();
                      }
                    }}
                    className="text-xs text-red-600 hover:text-red-800 flex items-center space-x-1"
                  >
                    <X className="w-3 h-3" />
                    <span>Clear all</span>
                  </motion.button>
                )}
                {unreadCount > 0 && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={markAllAsRead}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                  >
                    <CheckCheck className="w-3 h-3" />
                    <span>Mark all read</span>
                  </motion.button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-500">
                  <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p>No notifications yet</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                      !notification.isRead ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${
                            !notification.isRead ? 'text-gray-900' : 'text-gray-700'
                          }`}>
                            {notification.title}
                          </p>
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {notification.message}
                        </p>
                        <div className="flex items-center space-x-2 mt-2">
                          <div className="flex items-center space-x-1 text-xs text-gray-500">
                            <Clock className="w-3 h-3" />
                            <span>
                              {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                            </span>
                          </div>
                          {notification.employeeName && (
                            <div className="flex items-center space-x-1 text-xs text-gray-500">
                              <User className="w-3 h-3" />
                              <span>{notification.employeeName}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-200">
                <button
                  onClick={() => {
                    // Navigate based on role
                    if (user?.role === 'MANAGER') {
                      navigate('/manager/timesheets');
                    } else if (user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') {
                      navigate('/admin/timesheets/review');
                    } else {
                      navigate('/timesheets');
                    }
                    setIsOpen(false);
                  }}
                  className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  View All Timesheets
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationDropdown;
